import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, Edit2, Trash2, Power, PowerOff, 
  Database, TrendingUp, Lock, Unlock,
  AlertCircle, CheckCircle2, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BlockchainAssetRegistryPage = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    symbol: '',
    logo: '🪙',
    asset_type: 'custom_erc20',
    blockchain: 'polygon',
    contract_address: '',
    decimals: 18,
    description: '',
    deposit_enabled: true,
    trading_enabled: true,
    internal_transfer_enabled: true,
    withdrawal_enabled: true,
    auto_withdrawal_enabled: false,
    swap_enabled: true,
    is_settlement_asset: false,
    settlement_priority: 0,
    is_gas_asset: false,
    deposit_fee_percentage: 0,
    withdrawal_fee_percentage: 0,
    withdrawal_fee_fixed: 0,
    trading_fee_percentage: 0,
    min_withdrawal_amount: null,
    max_withdrawal_amount: null,
    min_confirmations: 12,
    block_explorer_url: '',
    treasury_wallet_address: ''
  });

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/blockchain/assets`, {
        credentials: 'include'
      });
      const data = await response.json();
      setAssets(data.assets || []);
    } catch (error) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingAsset 
        ? `${API_URL}/api/admin/blockchain/assets/${editingAsset.id}`
        : `${API_URL}/api/admin/blockchain/assets`;
      
      const method = editingAsset ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Operation failed');
      }

      toast.success(editingAsset ? 'Asset updated!' : 'Asset created!');
      setShowModal(false);
      setEditingAsset(null);
      resetForm();
      fetchAssets();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggleFeature = async (assetId, feature, currentValue) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/blockchain/assets/${assetId}/toggle/${feature}?enabled=${!currentValue}`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (!response.ok) throw new Error('Toggle failed');

      toast.success('Feature updated');
      fetchAssets();
    } catch (error) {
      toast.error('Failed to toggle feature');
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Deprecate this asset? (It will be marked as deprecated)')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/blockchain/assets/${assetId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Delete failed');

      toast.success('Asset deprecated');
      fetchAssets();
    } catch (error) {
      toast.error('Failed to deprecate asset');
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/blockchain/assets/seed`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Seed failed');

      const data = await response.json();
      toast.success(`Seeded ${data.total_assets} default assets`);
      fetchAssets();
    } catch (error) {
      toast.error('Failed to seed assets');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      symbol: '',
      logo: '🪙',
      asset_type: 'custom_erc20',
      blockchain: 'polygon',
      contract_address: '',
      decimals: 18,
      description: '',
      deposit_enabled: true,
      trading_enabled: true,
      internal_transfer_enabled: true,
      withdrawal_enabled: true,
      auto_withdrawal_enabled: false,
      swap_enabled: true,
      is_settlement_asset: false,
      settlement_priority: 0,
      is_gas_asset: false,
      deposit_fee_percentage: 0,
      withdrawal_fee_percentage: 0,
      withdrawal_fee_fixed: 0,
      trading_fee_percentage: 0,
      min_withdrawal_amount: null,
      max_withdrawal_amount: null,
      min_confirmations: 12,
      block_explorer_url: '',
      treasury_wallet_address: ''
    });
  };

  const openEditModal = (asset) => {
    setEditingAsset(asset);
    setFormData({
      name: asset.name,
      symbol: asset.symbol,
      logo: asset.logo,
      asset_type: asset.asset_type,
      blockchain: asset.blockchain,
      contract_address: asset.contract_address || '',
      decimals: asset.decimals,
      description: asset.description || '',
      deposit_enabled: asset.deposit_enabled,
      trading_enabled: asset.trading_enabled,
      internal_transfer_enabled: asset.internal_transfer_enabled,
      withdrawal_enabled: asset.withdrawal_enabled,
      auto_withdrawal_enabled: asset.auto_withdrawal_enabled,
      swap_enabled: asset.swap_enabled,
      is_settlement_asset: asset.is_settlement_asset,
      settlement_priority: asset.settlement_priority,
      is_gas_asset: asset.is_gas_asset,
      deposit_fee_percentage: asset.deposit_fee_percentage || 0,
      withdrawal_fee_percentage: asset.withdrawal_fee_percentage || 0,
      withdrawal_fee_fixed: asset.withdrawal_fee_fixed || 0,
      trading_fee_percentage: asset.trading_fee_percentage || 0,
      min_withdrawal_amount: asset.min_withdrawal_amount,
      max_withdrawal_amount: asset.max_withdrawal_amount,
      min_confirmations: asset.min_confirmations || 12,
      block_explorer_url: asset.block_explorer_url || '',
      treasury_wallet_address: asset.treasury_wallet_address || ''
    });
    setShowModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      deprecated: 'bg-red-100 text-red-800',
      pending: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Universal Asset Registry</h1>
            <p className="text-muted-foreground">Configure all blockchain assets - NO hard-coding</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSeedDefaults}>
              <Database className="h-4 w-4 mr-2" />
              Seed Defaults
            </Button>
            <Button onClick={() => { resetForm(); setEditingAsset(null); setShowModal(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Add Asset
            </Button>
          </div>
        </div>

        {/* Asset Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{assets.length}</div>
              <p className="text-sm text-muted-foreground">Total Assets</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">
                {assets.filter(a => a.status === 'active').length}
              </div>
              <p className="text-sm text-muted-foreground">Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">
                {assets.filter(a => a.is_settlement_asset).length}
              </div>
              <p className="text-sm text-muted-foreground">Settlement Assets</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-purple-600">
                {new Set(assets.map(a => a.blockchain)).size}
              </div>
              <p className="text-sm text-muted-foreground">Networks</p>
            </CardContent>
          </Card>
        </div>

        {/* Asset List */}
        <Card>
          <CardHeader>
            <CardTitle>Registered Assets</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading assets...</div>
            ) : assets.length === 0 ? (
              <div className="text-center py-12">
                <Database className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No assets configured</p>
                <Button className="mt-4" onClick={handleSeedDefaults}>Seed Default Assets</Button>
              </div>
            ) : (
              <div className="space-y-3">
                {assets.map((asset) => (
                  <div key={asset.id} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="text-4xl">{asset.logo}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold text-lg">{asset.name}</span>
                            <Badge variant="outline">{asset.symbol}</Badge>
                            <Badge className={getStatusColor(asset.status)}>{asset.status}</Badge>
                            {asset.is_settlement_asset && (
                              <Badge className="bg-blue-100 text-blue-800">Settlement</Badge>
                            )}
                            {asset.is_gas_asset && (
                              <Badge className="bg-purple-100 text-purple-800">Gas</Badge>
                            )}
                          </div>
                          
                          <div className="text-sm text-muted-foreground space-y-1">
                            <div><strong>Blockchain:</strong> {asset.blockchain}</div>
                            <div><strong>Type:</strong> {asset.asset_type}</div>
                            {asset.contract_address && (
                              <div className="font-mono text-xs">
                                <strong>Contract:</strong> {asset.contract_address.slice(0, 10)}...{asset.contract_address.slice(-8)}
                              </div>
                            )}
                          </div>

                          {/* Feature Toggles */}
                          <div className="flex flex-wrap gap-2 mt-3">
                            <Button
                              size="sm"
                              variant={asset.deposit_enabled ? "default" : "outline"}
                              onClick={() => handleToggleFeature(asset.id, 'deposit_enabled', asset.deposit_enabled)}
                            >
                              {asset.deposit_enabled ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                              Deposit
                            </Button>
                            <Button
                              size="sm"
                              variant={asset.withdrawal_enabled ? "default" : "outline"}
                              onClick={() => handleToggleFeature(asset.id, 'withdrawal_enabled', asset.withdrawal_enabled)}
                            >
                              {asset.withdrawal_enabled ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                              Withdrawal
                            </Button>
                            <Button
                              size="sm"
                              variant={asset.trading_enabled ? "default" : "outline"}
                              onClick={() => handleToggleFeature(asset.id, 'trading_enabled', asset.trading_enabled)}
                            >
                              {asset.trading_enabled ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                              Trading
                            </Button>
                            <Button
                              size="sm"
                              variant={asset.swap_enabled ? "default" : "outline"}
                              onClick={() => handleToggleFeature(asset.id, 'swap_enabled', asset.swap_enabled)}
                            >
                              {asset.swap_enabled ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                              Swap
                            </Button>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEditModal(asset)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(asset.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>{editingAsset ? 'Edit Asset' : 'Add New Asset'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Basic Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Asset Name *</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="e.g., USD Coin"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Symbol *</Label>
                        <Input
                          value={formData.symbol}
                          onChange={(e) => setFormData({...formData, symbol: e.target.value.toUpperCase()})}
                          placeholder="e.g., USDC"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Logo Emoji</Label>
                        <Input
                          value={formData.logo}
                          onChange={(e) => setFormData({...formData, logo: e.target.value})}
                          placeholder="🪙"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Type *</Label>
                        <select
                          value={formData.asset_type}
                          onChange={(e) => setFormData({...formData, asset_type: e.target.value})}
                          className="w-full border rounded-md p-2"
                        >
                          <option value="native_coin">Native Coin</option>
                          <option value="stablecoin">Stablecoin</option>
                          <option value="wrapped">Wrapped Asset</option>
                          <option value="utility">Utility Token</option>
                          <option value="governance">Governance Token</option>
                          <option value="custom_erc20">Custom ERC20</option>
                          <option value="ecosystem">Ecosystem Token</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Blockchain Configuration */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Blockchain Configuration</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Blockchain *</Label>
                        <select
                          value={formData.blockchain}
                          onChange={(e) => setFormData({...formData, blockchain: e.target.value})}
                          className="w-full border rounded-md p-2"
                          disabled={!!editingAsset}
                        >
                          <option value="polygon">Polygon</option>
                          <option value="ethereum">Ethereum</option>
                          <option value="base">Base</option>
                          <option value="bnb_chain">BNB Chain</option>
                          <option value="optimism">Optimism</option>
                          <option value="arbitrum">Arbitrum</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Decimals *</Label>
                        <Input
                          type="number"
                          value={formData.decimals}
                          onChange={(e) => setFormData({...formData, decimals: parseInt(e.target.value)})}
                          required
                        />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label>Contract Address</Label>
                        <Input
                          value={formData.contract_address}
                          onChange={(e) => setFormData({...formData, contract_address: e.target.value})}
                          placeholder="0x... (leave empty for native coins)"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Feature Flags */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Features</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { key: 'deposit_enabled', label: 'Enable Deposits' },
                        { key: 'withdrawal_enabled', label: 'Enable Withdrawals' },
                        { key: 'trading_enabled', label: 'Enable Trading' },
                        { key: 'swap_enabled', label: 'Enable Swaps' },
                        { key: 'internal_transfer_enabled', label: 'Enable Internal Transfers' },
                        { key: 'auto_withdrawal_enabled', label: 'Enable Auto Withdrawals' }
                      ].map(({ key, label }) => (
                        <label key={key} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData[key]}
                            onChange={(e) => setFormData({...formData, [key]: e.target.checked})}
                            className="rounded"
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Settlement & Priority */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Settlement & Priority</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.is_settlement_asset}
                          onChange={(e) => setFormData({...formData, is_settlement_asset: e.target.checked})}
                          className="rounded"
                        />
                        <span>Is Settlement Asset</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.is_gas_asset}
                          onChange={(e) => setFormData({...formData, is_gas_asset: e.target.checked})}
                          className="rounded"
                        />
                        <span>Is Gas Asset</span>
                      </label>
                      <div className="space-y-2">
                        <Label>Settlement Priority (Higher = More Preferred)</Label>
                        <Input
                          type="number"
                          value={formData.settlement_priority}
                          onChange={(e) => setFormData({...formData, settlement_priority: parseInt(e.target.value)})}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Fees */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Fee Configuration</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Withdrawal Fee %</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.withdrawal_fee_percentage}
                          onChange={(e) => setFormData({...formData, withdrawal_fee_percentage: parseFloat(e.target.value)})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Withdrawal Fee Fixed</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.withdrawal_fee_fixed}
                          onChange={(e) => setFormData({...formData, withdrawal_fee_fixed: parseFloat(e.target.value)})}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setShowModal(false); setEditingAsset(null); }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit">
                      {editingAsset ? 'Update Asset' : 'Create Asset'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
